import { useState } from 'react';
import { Alert, Box, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { parseMultiCompanySuccessEnvelope } from 'src/contracts/multiCompany/v1';
import { useWorkerProfile } from 'src/workspaces/workerProfile/WorkerProfileContext';
import { adminActionButtonSx, adminSecondaryActionButtonSx } from 'src/theme/adminActionButton';

type Work = { id: number; manualWorkerId: number; position: string; startDate: string;
  manualWorker: { firstName: string; lastName: string }; Event: { id: number; title: string };
  manualAccountLink: { workerAccountId: number } | null };
type WorkPage = { rows: Work[]; nextCursor: string | null };

/** An inline variation of the retained profile's table/actions; original work is never replaced. */
export default function ManualWorkLinks() {
  const scope = useWorkerProfile();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState<number | null>(null);
  const [error, setError] = useState('');
  const query = useInfiniteQuery<WorkPage>({
    queryKey: ['manual-work-links', scope?.basePath, search],
    enabled: Boolean(scope && open),
    queryFn: async ({ pageParam }) => {
      const response = await axios.get(`${scope!.basePath}/manual-work`, { params: { name: search, cursor: pageParam } });
      const data = parseMultiCompanySuccessEnvelope<WorkPage>(response.data)?.data;
      if (!data || !Array.isArray(data.rows)) throw new Error('Manual work could not be loaded. Search again in this profile.');
      return data;
    },
    getNextPageParam: last => last.nextCursor ?? undefined,
    retry: false
  });
  if (!scope) return null;
  const link = async (row: Work) => {
    setSaving(row.id); setError('');
    try {
      await axios.post(`${scope.basePath}/manual-work`, { jobId: row.id, manualWorkerId: row.manualWorkerId });
      await Promise.all([query.refetch(), client.invalidateQueries({ queryKey: ['company-worker-profile', scope.basePath] })]);
    } catch (cause: any) {
      const message = cause?.response?.data?.message;
      setError(typeof message === 'string' ? message : 'The work could not be linked. Search again and retry Link in this profile.');
    } finally { setSaving(null); }
  };
  return <Box sx={{ p: 2 }}>
    <Button size="small" sx={adminSecondaryActionButtonSx} variant="outlined" onClick={() => setOpen(value => !value)} aria-expanded={open}>
      {open ? 'Close Manual Work' : 'Link Manual Work'}
    </Button>
    {open && <Stack spacing={1} sx={{ mt: 1 }}>
      <Box component="form" onSubmit={event => { event.preventDefault(); setError(''); if (name.trim() === search) void query.refetch(); else setSearch(name.trim()); }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField size="small" label="Worker Name" value={name} onChange={event => setName(event.target.value)} inputProps={{ maxLength: 160 }} />
          <Button size="small" type="submit" variant="contained" sx={adminActionButtonSx}>Search</Button>
          <Button size="small" sx={adminSecondaryActionButtonSx} onClick={() => { setName(''); setSearch(''); }}>Linked Work</Button>
        </Stack>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {query.isError && <Alert severity="error">Manual work could not be loaded. Search again in this profile.</Alert>}
      {query.isLoading ? <Typography>Loading Manual Work…</Typography> : <TableContainer>
        <Table size="small" aria-label="Manual work history">
          <TableHead><TableRow><TableCell>Worker</TableCell><TableCell>Event</TableCell><TableCell>Position</TableCell><TableCell>Date</TableCell><TableCell align="right">Action</TableCell></TableRow></TableHead>
          <TableBody>{query.data?.pages.flatMap(page => page.rows).map(row => <TableRow key={row.id}>
            <TableCell>{row.manualWorker.firstName} {row.manualWorker.lastName}</TableCell>
            <TableCell>{row.Event.title}</TableCell><TableCell>{row.position}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(row.startDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}</TableCell>
            <TableCell align="right">{row.manualAccountLink ? <Typography variant="body2">{row.manualAccountLink.workerAccountId === scope.workerId ? 'Linked' : 'Linked To Another Account'}</Typography> :
              <Button size="small" sx={adminSecondaryActionButtonSx} variant="outlined" disabled={saving !== null} onClick={() => void link(row)}>{saving === row.id ? 'Linking…' : 'Link'}</Button>}</TableCell>
          </TableRow>)}</TableBody>
        </Table>
      </TableContainer>}
      {!query.isLoading && !query.isError && !query.data?.pages.some(page => page.rows.length) &&
        <Typography variant="body2">{search ? 'No matching manual work. Try another name.' : 'No manual work linked. Search by worker name.'}</Typography>}
      {query.hasNextPage && <Button size="small" sx={adminSecondaryActionButtonSx} disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>Load More</Button>}
    </Stack>}
  </Box>;
}
